/**
 * ConnectedTreemap -- wires URL params and Apollo queries to the Treemap
 * Chart component, replicating the exact JSX structure from the old chart.
 *
 * Data flow:
 *   URL params (usePageQueryParams)
 *     -> Apollo query (useQuery)
 *       -> data transformation (useTreemapDataTransformation)
 *         -> hierarchy (useTreemapHierarchy / useFilteredLeaves)
 *           -> Chart + Tooltip + Legend
 */

import { useRef, useState, useMemo, useCallback, memo, type ComponentProps } from "react";
import { useQuery } from "@apollo/client/react";
import type { DocumentNode } from "graphql";
import { uniqBy } from "lodash-es";
import type { HierarchyRectangularNode } from "d3-hierarchy";
import { useTreemapParams } from "../paramSchemas";
import { ProductLevel, ProductClass } from "@atlas-int/data/types";
import { getCategoriesForChartLegend } from "@atlas-int/viz";
import { getTradeDirectionSelector, ColorBy, LegendLabels, UIView, determineEndpointFacet, convertProductStringToNumberId, allProductsDatum, mapQueryArgumentsToAPIEndpointInputs, getExploreDataArray, toTradeDataItems } from "@atlas-int/data";
import { useVisualizationMetadata } from "../hooks/useVisualizationMetadata";
import type { ExploreDataRecord, ExploreDataQuery } from "@atlas-int/data";
import {
  useTreemapDataTransformation,
  useTreemapHierarchy,
  useFilteredLeaves,
  useHiddenCategories,
  useTooltipHandler,
  TreemapChart as Chart,
  ComplexityColorScaleLegend,
  type TreemapQueryVariables,
} from "@atlas-int/viz/treemap";
import { useProductYearMetadata } from "@atlas-int/data";
import type { MetadataFetchState, ProductComplexityPayload } from "@atlas-int/data/types";
import type { TradeDataItem, EnrichedTradeDataItem } from "@atlas-int/data/types";
import type { CategoryDatum } from "@atlas-int/viz/shared";
import type { FindInVizOption } from "@atlas-int/controls";
import TreemapTooltipContent from "./TreemapTooltipSection";
import {
  useVisualizationState,
  VisualizationContent,
  LegendRow,
  GenericResizeContainer,
  TooltipStateIsolationProvider,
  TooltipStateBridge,
} from "@atlas-int/viz/shared";
import { VisualizationContentContainer } from "../VizGrid";
import { CategoryLabels } from "@atlas-int/viz/shared";
import VisualizationLayout from "../components/VisualizationLayout";

const TreemapChartWithTooltip = memo(function TreemapChartWithTooltip({
  setHighlightedItem,
  ...chartProps
}: { setHighlightedItem: (item: string | undefined) => void } & Omit<ComponentProps<typeof Chart>, "handleTooltip">) {
  const { onHover } = useTooltipHandler(setHighlightedItem);
  return <Chart {...chartProps} handleTooltip={onHover} />;
});

// Sort functions for find-in-viz options
const sortByLocationName = (a: EnrichedTradeDataItem, b: EnrichedTradeDataItem) =>
  ((a.nameEn || a.groupName || "") as string).localeCompare((b.nameEn || b.groupName || "") as string);
const sortByProductCode = (a: EnrichedTradeDataItem, b: EnrichedTradeDataItem) =>
  ((a.productCode || "") as string).localeCompare((b.productCode || "") as string);

/**
 * ConnectedTreemap reads all settings from URL query params, fetches data
 * via Apollo, transforms it, and renders Chart with the old chart's exact
 * JSX structure.
 */
const ConnectedTreemap: React.FC = () => {
  // === URL PARAMS ===
  const [
    {
      exporter,
      importer,
      product: productId,
      view,
      productClass,
      productLevel,
      year,
      colorBy,
      locationLevel,
      tradeFlow,
      servicesClass,
      tradeDirection,
    },
  ] = useTreemapParams();

  // === STATE ===
  const { hiddenCategories, setHiddenCategories } = useHiddenCategories(
    view,
    colorBy,
  );
  const [highlightedItem, setHighlightedItem] = useState<string | undefined>();

  // === REFS ===
  const treemapElement = useRef<HTMLDivElement | null>(null);

  // === METADATA ===
  const { locationMetadata, productMetadata } =
    useVisualizationMetadata();
  const productComplexityMetadata = useProductYearMetadata({
    yearRange: { yearMin: year ?? undefined, yearMax: year ?? undefined },
    productClass,
    productLevel: ProductLevel.Product4digit,
  }) as MetadataFetchState<ProductComplexityPayload>;

  // === QUERY BUILDING ===
  const tradeDirectionSelector = getTradeDirectionSelector({
    tradeDirection,
  });

  const useProductLevel = useMemo(() => {
    if (view === UIView.Markets) {
      return productId === allProductsDatum.productId
        ? ProductLevel.ProductSection
        : undefined;
    }
    return productLevel;
  }, [view, productId, productLevel]);

  const narrowedInputVariables = useMemo(
    () => mapQueryArgumentsToAPIEndpointInputs({ exporter, importer, view }),
    [exporter, importer, view],
  );

  const queryVariables = useMemo(
    () => ({
      productClass,
      yearMax: year,
      yearMin: year,
      productLevel: useProductLevel,
      servicesClass,
      productId:
        productId === allProductsDatum.productId
          ? undefined
          : convertProductStringToNumberId(productId),
      ...narrowedInputVariables,
    }),
    [
      productClass,
      year,
      useProductLevel,
      servicesClass,
      productId,
      narrowedInputVariables,
    ],
  );

  const { queryToUse, queryIsInvalid } = useMemo(
    () => determineEndpointFacet({ view, variables: queryVariables }),
    [view, queryVariables],
  );

  // === DATA FETCHING ===
  const { loading: queryLoading, error, data, previousData } = useQuery(
    queryToUse as DocumentNode,
    { variables: queryVariables, skip: queryIsInvalid },
  );
  const inputData = error ? data : (data || previousData);

  // Include metadata loading in overall loading state so the spinner stays
  // visible until the chart has everything it needs to render.
  const activeProductMetadata =
    productClass === ProductClass.HS92Products ? productMetadata.hs92
    : productClass === ProductClass.HS12Products ? productMetadata.hs12
    : productClass === ProductClass.HS22Products ? productMetadata.hs22
    : productMetadata.sitc;
  const metadataLoading =
    locationMetadata.status !== "success" ||
    activeProductMetadata.status !== "success" ||
    productComplexityMetadata.status !== "success";
  const loading = queryLoading || metadataLoading;

  const narrowedChartInput = useMemo((): { data: TradeDataItem[] } => {
    if (!inputData) return { data: [] };
    const rows =
      getExploreDataArray(inputData as ExploreDataQuery) ?? [];
    return { data: toTradeDataItems(rows) };
  }, [inputData]);

  /** Drives tooltip reset when URL, legend filters, or fetched rows change (see useTooltipState dataVersion). */
  const tooltipDataVersion = useMemo(
    () => ({
      queryVariables,
      tradeDirection,
      tradeFlow,
      colorBy,
      locationLevel,
      hiddenCategories,
      chartData: narrowedChartInput.data,
    }),
    [
      queryVariables,
      tradeDirection,
      tradeFlow,
      colorBy,
      locationLevel,
      hiddenCategories,
      narrowedChartInput,
    ],
  );

  // === DATA TRANSFORMATION ===
  const memoizedQueryVariables = useMemo(
    () => ({
      view,
      productClass,
      productLevel,
      exporter,
      importer,
      year,
      tradeFlow,
      colorBy,
      locationLevel,
    }),
    [
      view,
      productClass,
      productLevel,
      exporter,
      importer,
      year,
      tradeFlow,
      colorBy,
      locationLevel,
    ],
  );

  const dataTransformationResult = useTreemapDataTransformation({
    inputData: narrowedChartInput,
    queryVariables: { ...memoizedQueryVariables, year: year ?? undefined } satisfies TreemapQueryVariables,
    hiddenCategories,
    tradeDirection,
    tradeFlow,
    tradeDirectionSelector,
    locationMetadata,
    productMetadata,
    productComplexityMetadata,
  });

  // === HIERARCHY ===
  const hierarchyResult = useTreemapHierarchy({
    dataTransformationResult,
    chartWidth: 800,
    chartHeight: 600,
    view,
    tradeFlow,
    tradeDirection,
    tradeDirectionSelector,
  });

  const { root, servicesRoot, nonServicesRoot, totalSum, locationHasServices } =
    hierarchyResult;

  const {
    filteredRootLeaves,
    filteredServicesRootLeaves,
    filteredNonServicesRootLeaves,
  } = useFilteredLeaves({
    root,
    servicesRoot,
    nonServicesRoot,
    view,
    locationHasServices,
  });

  // === DERIVED VALUES ===
  const totalValue = totalSum;

  const findInVizOptions: FindInVizOption[] = useMemo(() => {
    const leaves: HierarchyRectangularNode<EnrichedTradeDataItem>[] = [];
    if (locationHasServices) {
      if (filteredServicesRootLeaves)
        leaves.push(...filteredServicesRootLeaves);
      if (filteredNonServicesRootLeaves)
        leaves.push(...filteredNonServicesRootLeaves);
    } else {
      if (filteredRootLeaves) leaves.push(...filteredRootLeaves);
    }

    const visibleLeaves = leaves
      .filter((l) => l.value! > 0)
      .map((l) => l.data);

    if (!visibleLeaves.length) return [];

    if (view === UIView.Markets) {
      return uniqBy(
        visibleLeaves
          .filter((loc) => loc.id && (loc.nameEn || loc.groupName))
          .sort(sortByLocationName)
          .map((loc) => ({
            id: loc.id,
            label: loc.countryIso3Code
              ? `${loc.nameEn} (${loc.countryIso3Code})`
              : loc.nameEn || loc.groupName,
          }))
          .filter((opt) => opt.label),
        "id",
      ) as FindInVizOption[];
    } else {
      return uniqBy(
        visibleLeaves
          .filter((p: EnrichedTradeDataItem) => p.productId && p.nameEn)
          .sort(sortByProductCode)
          .map((p: EnrichedTradeDataItem) => ({
            id: p.productId,
            label: p.productCode
              ? `${p.nameEn} (${p.productCode} ${productClass})`
              : p.nameEn,
          })),
        "id",
      ) as FindInVizOption[];
    }
  }, [
    filteredRootLeaves,
    filteredServicesRootLeaves,
    filteredNonServicesRootLeaves,
    locationHasServices,
    view,
    productClass,
  ]);

  // === LEGEND ===
  const categoriesForLegend: CategoryDatum[] = getCategoriesForChartLegend({
    view,
    productClass,
  });

  let treemapLegend: React.ReactNode;

  if (view === UIView.Products && colorBy === ColorBy.Complexity) {
    treemapLegend = <ComplexityColorScaleLegend />;
  } else {
    const resetText =
      view === UIView.Markets ? "Show All Regions" : "Show All Sectors";
    const treemapLegendLabel =
      view === UIView.Markets ? LegendLabels.Region : LegendLabels.Sector;
    treemapLegend = (
      <CategoryLabels
        legendLabel={treemapLegendLabel}
        categories={categoriesForLegend}
        allowToggle={true}
        hiddenCategories={hiddenCategories}
        setHiddenCategories={setHiddenCategories}
        resetText={resetText}
        fullWidth={true}
      />
    );
  }

  // === CONTENT ===
  const vizState = useVisualizationState({
    loading,
    error,
    data: inputData ? narrowedChartInput : undefined,
    previousData: undefined,
    totalValue,
  });
  const hasValidSelection = exporter || importer || productId;

  const clearHighlightedItem = useCallback(
    () => setHighlightedItem(undefined),
    [],
  );

  return (
    <VisualizationLayout
      totalValue={totalValue}
      findInVizOptions={findInVizOptions}
      highlightedItem={highlightedItem}
      onHighlight={setHighlightedItem}
    >
      <GenericResizeContainer chartContainerRef={treemapElement}>
        <TooltipStateIsolationProvider
          dataVersion={tooltipDataVersion}
          onHighlight={setHighlightedItem}
        >
          <TooltipStateBridge
            tooltipContent={
              <TreemapTooltipContent
                view={view}
                totalValue={totalValue}
                tradeDirection={tradeDirection}
                productClass={productClass}
                loading={loading}
                hasError={vizState.hasError}
                hasNoData={vizState.hasNoData || vizState.hasZeroTrade}
                hasVizContent={Boolean(hasValidSelection && inputData)}
              />
            }
            cursor="default"
            overrideStyles={true}
            clearHighlightedItem={clearHighlightedItem}
            portalContainerId="overlayPortalContainerId"
          >
            <VisualizationContentContainer ref={treemapElement}>
              <VisualizationContent
                hasError={vizState.hasError}
                errorNoticeType={vizState.errorNoticeType}
                hasNoData={vizState.hasNoData}
                hasZeroTrade={vizState.hasZeroTrade}
                loading={loading}
              >
                {hasValidSelection && inputData ? (
                  <TreemapChartWithTooltip
                    setHighlightedItem={setHighlightedItem}
                    inputData={narrowedChartInput}
                    queryVariables={{ ...memoizedQueryVariables, year: year ?? undefined } satisfies TreemapQueryVariables}
                    hiddenCategories={hiddenCategories}
                    highlightedItem={highlightedItem}
                    tradeDirection={tradeDirection}
                    isLoading={loading}
                    locationMetadata={locationMetadata}
                    productMetadata={productMetadata}
                    productComplexityMetadata={productComplexityMetadata}
                  />
                ) : null}
              </VisualizationContent>
            </VisualizationContentContainer>
          </TooltipStateBridge>
        </TooltipStateIsolationProvider>
        <LegendRow>
          {treemapLegend}
        </LegendRow>
      </GenericResizeContainer>
    </VisualizationLayout>
  );
};

export default memo(ConnectedTreemap);
